import RetailSaleNotesDialog from "./RetailSaleNotesDialog";
import {
    Add,
    ArrowBack,
    CheckCircleOutline,
    DeleteForever,
    Notes,
    Person,
    PersonAddAlt1,
    Place,
    Print,
} from "@mui/icons-material";
import { useStyles } from "./styles";

import styled from "@emotion/styled";
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    Switch,
    emphasize,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../../../AppContext";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import Scrollbar from "../../../components/Scrollbar/Scrollbar";
import { SimpleSnackbarText } from "../../../components/SimpleSnackbar/SimpleSnackbar";
import { isRoleAdmin, isRoleService } from "../../../frappeConfig";
import { PosContext } from "../../../shared/PosContext";
import {
    getOrderDoctype,
    isDraftTableOrder,
} from "../../../shared/draftOrderUtils";
import { useUpdateDoctypeData } from "../../../shared/hooks/useDocTypesData";
import { getCurrentDate } from "../../../utils/dateUtils";
import { getOrderShortName, parseAddress } from "../../../utils/floorUtils";
import PrintOrderModal from "../../Tables/components/PrintOrderModal";
import tableIcon from "./../../../images/table-icons.svg";
import reservedTableIcon from "./../../../images/table-reserved-icon.svg";
import CustomerDialog from "./CustomerDialog/CustomerDialog";
import CustomerManagementDialog from "./CustomerManagementDialog";
import OrderCartItem from "./OrderCartItem";
import OrderDetailsDialog from "./OrderDetailsDialog";
import OrderNotesDialog from "./OrderNotesDialog";
import { calculateMaterializedAddonTotal } from "../../Selling/Widgets/AddItemWidget/addonTree";
import { isAddonChildCartEntry } from "./retailReturnUtils";

const OrderCart = ({
    handleSelectOrder,
    handleCloseTable,
    handleSaveToTable,
    handleSaveToTableDraft,
    handleSendDraftOrder,
    isSendingToKitchen = false,
    isSavingDraft = false,
    isSendingDraftOrder = false,
    table,
    items,
    savedItems,
    selectedOrder,
    minified = false,
    isOrderItemsLoading,
    isTakeaway,
    onItemEdit,
    onItemSplit,
    onItemDelete,
    onSubmittedItemDelete,
    itemDeleting,
    isDeletingOrder = false,
    onDeleteOrder,
    deletingItemIdentifier,
    hasDraftPendingChanges = false,
    isRetailPos = false,
    onNewRetailSale,
    onOpenRetailDrafts,
    onAddOrder,
    showNewRetailSale = false,
    retailReturnActive = false,
    retailReturnContent = null,
    isKioskMode = false,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const {
        customerContext,
        addressContext,
        addresses,
        orders,
        refetchCustomers,
        refetchAddresses,
        refetchTablesData,
    } = useContext(PosContext);

    const navigate = useNavigate();

    const [retailNotesOpen, setRetailNotesOpen] = useState(false);
    const [retailNotesSaving, setRetailNotesSaving] = useState(false);
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const toggleOrderDetails = () => setOrderDetailsOpen(!orderDetailsOpen);

    const [changeOrderOpen, setChangeOrderOpen] = useState(false);
    const toggleChangeOrder = () => setChangeOrderOpen((v) => !v);

    const [customerMgmtOpen, setCustomerMgmtOpen] = useState(false);

    const closeCustomerMgmt = () => setCustomerMgmtOpen(false);

    const [vat, setVat] = useState(0);
    const [subTotal, setSubTotal] = useState(0);
    const [total, setTotal] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = customerContext;
    const [selectedAddress, setSelectedAddress] = addressContext;
    const { tableName, orderName } = useParams();
    const {
        userInfo,
        isTabsPos,
        serviceFee,
        elevatedPrivileges,
        restaurantSettings,
        foodLateTimeEnabled,
        isCallCenterUser,
    } = useContext(AppContext);

    const isMutating = useIsMutating();
    const isServiceUser = isRoleService(userInfo?.role_profile_name);

    const [printOrderModal, setPrintOrderModal] = useState(false);
    const togglePrintOrder = () => setPrintOrderModal(!printOrderModal);

    const [canSendToKitchen, setCanSendToKitchen] = useState(true);
    const [draftModeEnabled, setDraftModeEnabled] = useState(false);

    // Guard against accidental double-submit clicks around mutation success window
    const [sendButtonLocked, setSendButtonLocked] = useState(false);
    const postSuccessUnlockTimeoutRef = useRef(null);

    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const [orderNotesDialogOpen, setOrderNotesDialogOpen] = useState(false);
    const [locallyReadyOrders, setLocallyReadyOrders] = useState({});
    const [highlightedItemKey, setHighlightedItemKey] = useState(null);
    const orderItemsRef = useRef(null);
    const itemRefs = useRef({});
    const highlightTimeoutRef = useRef(null);
    const prevItemsLengthRef = useRef(null);

    const updateDoctype = useUpdateDoctypeData();
    const markFoodReadyMutation = useUpdateDoctypeData();

    const hasLocallyMarkedFoodReady =
        selectedOrder &&
        locallyReadyOrders &&
        locallyReadyOrders[selectedOrder.name];

    const canMarkFoodReady =
        foodLateTimeEnabled &&
        selectedOrder &&
        !selectedOrder?.food_ready_time &&
        !hasLocallyMarkedFoodReady;

    useEffect(() => {
        if (!selectedOrder?.name) {
            return;
        }
        if (selectedOrder?.food_ready_time) {
            setLocallyReadyOrders((prev) => {
                if (prev[selectedOrder.name]) {
                    return prev;
                }
                return {
                    ...prev,
                    [selectedOrder.name]: true,
                };
            });
        } else {
            setLocallyReadyOrders((prev) => {
                if (!prev[selectedOrder.name]) {
                    return prev;
                }
                const updated = { ...prev };
                delete updated[selectedOrder.name];
                return updated;
            });
        }
    }, [selectedOrder?.name, selectedOrder?.food_ready_time]);

    const handleMarkFoodReady = () => {
        if (!selectedOrder || markFoodReadyMutation.isLoading) {
            return;
        }
        const currentOrderName = selectedOrder.name;
        const payload = {
            food_ready_time: getCurrentDate(),
        };
        markFoodReadyMutation.mutate(
            {
                doctype: "Table Order",
                name: selectedOrder.name,
                formData: JSON.stringify(payload),
            },
            {
                onSuccess: () => {
                    if (currentOrderName) {
                        setLocallyReadyOrders((prev) => ({
                            ...prev,
                            [currentOrderName]: true,
                        }));
                    }
                    refetchTablesData();
                    enqueueSnackbar(
                        <SimpleSnackbarText label="Order marked ready" />,
                        { variant: "success" }
                    );
                },
                onError: (error) => {
                    console.error("Failed to mark order ready", error);
                },
            }
        );
    };

    useEffect(() => {
        if (!isTakeaway) {
            setSelectedCustomer(selectedOrder?.customer);
            setSelectedAddress(selectedOrder?.address);
        } else {
            if (selectedOrder?.customer) {
                setSelectedCustomer(selectedOrder?.customer);
                setSelectedAddress(selectedOrder?.address);
            }
        }
    }, [selectedOrder, isTakeaway, selectedOrder?.customer]);

    useEffect(() => {
        let addonsTotal = 0;
        let total = 0;
        items
            .filter((item) => !isAddonChildCartEntry(item))
            .forEach((item) => {
                total += item.qty * (item.item.standard_rate ?? item.item.rate);

                addonsTotal += calculateMaterializedAddonTotal(
                    item.addons || [],
                    item.qty
                );
            });

        savedItems.forEach((item) => {
            total += item.qty * (item.item.rate ?? item.item.standard_rate);

            addonsTotal += calculateMaterializedAddonTotal(
                item.addons || [],
                item.qty
            );
        });

        addonsTotal = addonsTotal || 0;

        if (serviceFee) {
            const subTotal = total + addonsTotal;

            const serviceFeeAmount = Number(
                Math.ceil(subTotal * (serviceFee / 100))
            );
            total = subTotal + serviceFeeAmount;
            if (selectedOrder?.discount > 0) {
                total = total - Math.ceil(selectedOrder?.discount);
            }
            setTotal(Math.ceil(total));
            setSubTotal(subTotal);
            setVat(serviceFeeAmount);
        } else {
            const _discount = selectedOrder?.discount || 0;
            console.log("_discount", _discount);
            console.log(
                "total + addonsTotal - _discount",
                total + addonsTotal - _discount
            );

            setSubTotal(total + addonsTotal);
            setTotal(total + addonsTotal - _discount);
        }

        setCanSendToKitchen(checkCanSendToKitchen());
    }, [items, savedItems, serviceFee]);

    const onSave = async () => {
        if (sendButtonLocked) return;
        setSendButtonLocked(true);
        if (isTakeaway && !selectedCustomer) {
            enqueueSnackbar(
                <SimpleSnackbarText label={t("pos.selectCustomer")} /> //TODO FIX THIS :'_)
            );
            // Allow retry when validation fails
            setSendButtonLocked(false);
            return;
        }
        if (handleSaveToTable) {
            handleSaveToTable(table);
        }
    };

    const onSaveDraft = async () => {
        if (sendButtonLocked) return;
        setSendButtonLocked(true);
        if (isTakeaway && !selectedCustomer) {
            enqueueSnackbar(
                <SimpleSnackbarText label={t("pos.selectCustomer")} />
            );
            setSendButtonLocked(false);
            return;
        }
        if (handleSaveToTableDraft) {
            handleSaveToTableDraft(table);
        } else {
            // If the feature isn't enabled in this render context, unlock immediately.
            setSendButtonLocked(false);
        }
    };

    const disablePrimaryActions =
        sendButtonLocked ||
        isSavingDraft ||
        isSendingToKitchen ||
        isMutating >= 1;

    const canShowDraftToggle =
        !isRetailPos &&
        !isKioskMode &&
        isTabsPos &&
        !!handleSaveToTableDraft &&
        // Draft is only for "new" orders (no existing order / no saved items)
        !selectedOrder &&
        (!savedItems || savedItems.length === 0);

    const isDraftOrder = isDraftTableOrder(selectedOrder);
    const canDeleteOrder =
        !!selectedOrder &&
        (isDraftOrder ||
            (isRetailPos && selectedOrder.status === "Attending") ||
            elevatedPrivileges ||
            isRoleAdmin(userInfo.role_profile_name));
    const disableDraftOrderActions =
        isSendingDraftOrder ||
        isSavingDraft ||
        isSendingToKitchen ||
        isMutating >= 1;
    const isCheckoutBlocked =
        !!isOrderItemsLoading ||
        sendButtonLocked ||
        isSendingToKitchen ||
        isSavingDraft ||
        isSendingDraftOrder ||
        itemDeleting ||
        isDeletingOrder;

    // For reopened draft orders, the primary action is "Save" (save new items to draft).
    // For new orders with draft toggle on, the primary action is "Save as Draft".
    // Otherwise, the primary action is "Send to Kitchen".
    const primaryActionLabel = isKioskMode
        ? t("kiosk.payCash")
        : isRetailPos
        ? isDraftOrder
            ? t("common.save")
            : isServiceUser
            ? t("pos.sendToKitchen")
            : t("retailPos.saveSale")
        : isDraftOrder
        ? t("common.save")
        : draftModeEnabled
        ? t("pos.saveAsDraft")
        : t("pos.sendToKitchen");
    const primaryActionLoading = isKioskMode
        ? isSavingDraft
        : isDraftOrder
        ? isSavingDraft
        : draftModeEnabled
        ? isSavingDraft
        : isSendingToKitchen;
    const onPrimaryAction = isKioskMode
        ? onSaveDraft
        : isDraftOrder
        ? onSaveDraft
        : !isRetailPos && draftModeEnabled
        ? onSaveDraft
        : onSave;
    const canShowPrimaryAction =
        (isRetailPos && items && items.length > 0) ||
        canSendToKitchen ||
        (isDraftOrder && hasDraftPendingChanges);

    // After mutation finishes, keep the button locked briefly to avoid double click
    useEffect(() => {
        if (sendButtonLocked && isMutating === 0) {
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
            }
            postSuccessUnlockTimeoutRef.current = setTimeout(() => {
                setSendButtonLocked(false);
                postSuccessUnlockTimeoutRef.current = null;
            }, 1200);
        }
        return () => {
            // cleanup on effect rerun
        };
    }, [isMutating, sendButtonLocked]);

    // If items were cleared or sending became no longer possible, unlock immediately
    useEffect(() => {
        if (!canShowPrimaryAction && sendButtonLocked) {
            setSendButtonLocked(false);
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
                postSuccessUnlockTimeoutRef.current = null;
            }
        }
    }, [canShowPrimaryAction, sendButtonLocked]);

    // When switching orders, reset the lock
    useEffect(() => {
        setSendButtonLocked(false);
        if (postSuccessUnlockTimeoutRef.current) {
            clearTimeout(postSuccessUnlockTimeoutRef.current);
            postSuccessUnlockTimeoutRef.current = null;
        }
        setDraftModeEnabled(false);
    }, [selectedOrder?.name]);

    // If cart is cleared, exit draft mode
    useEffect(() => {
        if (!items || items.length === 0) {
            setDraftModeEnabled(false);
        }
    }, [items?.length]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (postSuccessUnlockTimeoutRef.current) {
                clearTimeout(postSuccessUnlockTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!items || items.length === 0) {
            prevItemsLengthRef.current = items?.length || 0;
            setHighlightedItemKey(null);
            return;
        }

        const currentLength = items.length;
        const prevLength = prevItemsLengthRef.current;
        const lastIndex = currentLength - 1;
        const lastKey = getItemKey(items[lastIndex], lastIndex);

        if (prevLength !== null && currentLength > prevLength) {
            setHighlightedItemKey(lastKey);
        }

        prevItemsLengthRef.current = currentLength;
    }, [items]);

    useEffect(() => {
        if (!highlightedItemKey) return;

        const node = itemRefs.current[highlightedItemKey];
        const scrollContainer = getScrollContainer();

        if (
            node &&
            scrollContainer &&
            scrollContainer.scrollHeight > scrollContainer.clientHeight
        ) {
            node.scrollIntoView({ behavior: "smooth", block: "end" });
        }

        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedItemKey(null);
            highlightTimeoutRef.current = null;
        }, 500);

        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
                highlightTimeoutRef.current = null;
            }
        };
    }, [highlightedItemKey]);

    const handleEditItem = (item) => {
        onItemEdit(item);
    };

    const handleDeleteItem = (item, props) => {
        onItemDelete(item, props);
    };

    const handleDeleteSubmittedItem = (item, password, qty, note, waste) => {
        onSubmittedItemDelete(item, password, qty, note, waste);
    };

    const getItemKey = (item, index) =>
        item?.entry_name ||
        item?.item?.identifier ||
        `${item?.item?.item_code || "item"}-${index}`;

    const setItemRef = useCallback(
        (key) => (node) => {
            if (!key) return;
            if (node) {
                itemRefs.current[key] = node;
            } else {
                delete itemRefs.current[key];
            }
        },
        []
    );

    const getScrollContainer = () => {
        const container = orderItemsRef.current;
        if (!container) return null;
        return (
            container.querySelector(".simplebar-content-wrapper") || container
        );
    };

    // Weather or not we have to send to kitchen.
    const checkCanSendToKitchen = () => {
        if (!items || items.length == 0) return false;

        for (const item of items) {
            if (item.item.status === "Attending") {
                return true;
            }
        }
        return false;
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    const assignCustomerToTable = (customerName, address) => {
        if (selectedOrder) {
            updateTableOrder(customerName, address);
        }
    };

    const handleDeleteOrder = () => {
        if (!selectedOrder || isDeletingOrder) {
            return;
        }

        // Drafts and retail parked sales can be deleted without the
        // restaurant-specific notes/waste confirmation flow.
        if (selectedOrder.status === "Draft" || isRetailPos) {
            onDeleteOrder?.({
                orderId: selectedOrder.name,
                notes: "",
                waste: false,
            });
            return;
        }

        setDeleteConfirmOpen(true);
    };

    const confirmDeleteOrder = (password, qty, notes, waste) => {
        if (selectedOrder) {
            onDeleteOrder?.({
                orderId: selectedOrder.name,
                masterPassword: password,
                notes: notes,
                waste: waste,
            });
        }
    };

    const cancelDeleteOrder = () => {
        setDeleteConfirmOpen(false);
    };

    const handleEditOrderNotes = () => {
        if (selectedOrder) {
            setOrderNotesDialogOpen(true);
        }
    };

    const handleSaveOrderNotes = async (formData) => {
        if (selectedOrder) {
            if (isTabsPos && isDraftOrder && handleSaveToTableDraft) {
                await handleSaveToTableDraft(table, { promptData: formData });
                setOrderNotesDialogOpen(false);
                return;
            }

            updateDoctype.mutate(
                {
                    doctype: getOrderDoctype(selectedOrder),
                    name: selectedOrder.name,
                    formData: JSON.stringify(formData),
                },
                {
                    onSuccess: () => {
                        refetchTablesData();
                        setOrderNotesDialogOpen(false);
                    },
                }
            );
        }
    };

    const handleCloseOrderNotes = () => {
        setOrderNotesDialogOpen(false);
    };

    const onConfirmCustomerChanged = (customerName, address) => {
        refetchCustomers();
        refetchAddresses();
        setSelectedCustomer(customerName);
        if (address) {
            setSelectedAddress(address);
        }
        setChangeCustomerDialogOpen(false);
        assignCustomerToTable(customerName, address);
    };

    const onTableClose = () => {
        if ((items && items.length > 0) || savedItems.length > 0) {
            if (handleCloseTable) handleCloseTable(selectedOrder, table);
        }
    };

    const onSelectOrder = (order) => {
        if (handleSelectOrder) {
            handleSelectOrder(order);
        }
    };

    const closePos = () => {
        navigate(isRetailPos ? "/pos" : "/tables");
    };

    const tableOrderList = useMemo(() => {
        if (!table?.orders) return [];
        return Object.values(table.orders);
    }, [table?.orders]);

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    const updateTableOrder = (customer, address) => {
        const payload = {
            customer,
        };

        if (address) {
            payload.address = address;
        }
        updateDoctype.mutate(
            {
                doctype: "Table Order",
                name: selectedOrder.name,
                formData: JSON.stringify(payload),
            },
            {
                onSuccess: () => {
                    refetchTablesData();
                    setChangeCustomerDialogOpen(false);
                },
            }
        );
    };

    const onCustomerSelected = (customer, address) => {
        setSelectedCustomer(customer);
        setSelectedAddress(address);

        if (selectedOrder) {
            if (address) {
                updateTableOrder(customer, address);
                // assignCustomerToTable(customer, address);
            } else {
                updateTableOrder(customer);
            }
            refetchTablesData();
        }

        setChangeCustomerDialogOpen(false);
    };

    const CustomerField = () => {
        const handleClick = () => {
            toggleOrderDetails();
        };

        if (!selectedAddress && selectedCustomer) {
            return (
                <div className="DetailsGroup">
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick();
                        }}
                    >
                        <Person className="DetailsIcon" />
                        {selectedCustomer}
                    </div>
                </div>
            );
        } else if (selectedAddress && selectedCustomer) {
            return (
                <div onClick={handleClick}>
                    <div className="DetailsGroup">
                        <Person className="DetailsIcon" /> {selectedCustomer}
                    </div>
                    <div className="DetailsGroup">
                        <Place className="DetailsIcon" />
                        {parseAddress(
                            addresses[selectedCustomer]?.find(
                                (i) => i.name === selectedAddress
                            )
                        )}
                    </div>
                </div>
            );
        } else {
            return "";
        }
    };

    return (
        <>
            <div
                className={`${classes.OrderWidget} ${
                    isRetailPos ? classes.RetailOrderWidget : ""
                }`}
            >
                {isDeletingOrder && <LinearProgress />}
                <div>
                    <div className={classes.TableDetails}>
                        {isRetailPos || isKioskMode ? null : table.reserved ? (
                            <div className={classes.Table_ReservedWrap}>
                                <div>
                                    <img src={reservedTableIcon} />
                                </div>
                            </div>
                        ) : (
                            <div className="TableIcon">
                                {" "}
                                <img src={tableIcon} />
                            </div>
                        )}
                        <div className="TableName">
                            <div>
                                {isRetailPos || isTabsPos || isKioskMode
                                    ? ``
                                    : `${table.tableDescription}`}
                            </div>
                            <div>
                                {isRetailPos
                                    ? getOrderShortName(
                                          selectedOrder?.name || orderName
                                      ) || t("retailPos.newSale")
                                    : isTabsPos || isKioskMode
                                    ? getOrderShortName(orderName) ||
                                      t("pos.newOrder")
                                    : t("pos.tableDetails")}
                            </div>
                        </div>
                        {retailReturnActive ? (
                            <Chip
                                size="small"
                                color="error"
                                label={t("retailReturn.badge")}
                                sx={{ fontWeight: 800 }}
                            />
                        ) : null}
                        {canShowDraftToggle ? (
                            <div
                                className="DraftToggle"
                                title={t("pos.draftModeHint")}
                            >
                                <span className="DraftToggle__Label">
                                    {t("pos.draft")}
                                </span>
                                <Switch
                                    size="small"
                                    checked={draftModeEnabled}
                                    onChange={(e) =>
                                        setDraftModeEnabled(e.target.checked)
                                    }
                                    disabled={
                                        disablePrimaryActions ||
                                        !items ||
                                        items.length === 0
                                    }
                                />
                            </div>
                        ) : null}
                        {false && isTabsPos ? (
                            <div style={{ marginRight: "0.5rem" }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={toggleChangeOrder}
                                >
                                    {t("pos.changeOrder")}
                                </Button>
                            </div>
                        ) : null}
                        {isServiceUser && onAddOrder ? (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={onAddOrder}
                                sx={{
                                    marginRight: "0.5rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {t("pos.addOrder")}
                            </Button>
                        ) : null}
                        {!retailReturnActive && !isKioskMode ? (
                            <div
                                className="HeaderPrint"
                                style={{ marginRight: "1rem" }}
                            >
                                <Button onClick={togglePrintOrder}>
                                    <Print />
                                </Button>
                            </div>
                        ) : null}
                        {isRetailPos &&
                        !isServiceUser &&
                        !retailReturnActive ? (
                            <div
                                style={{
                                    display: "flex",
                                    gap: "0.35rem",
                                    marginRight: "0.5rem",
                                }}
                            >
                                {showNewRetailSale ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={onNewRetailSale}
                                    >
                                        {t("retailPos.newSale")}
                                    </Button>
                                ) : null}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={onOpenRetailDrafts}
                                >
                                    {t("retailPos.draftSales")}
                                </Button>
                            </div>
                        ) : null}
                        {/* <div style={{ marginRight: "0.5rem" }}>
                            <Button onClick={() => openCustomerMgmt(0)}>
                                <ManageAccounts />
                            </Button>
                        </div> */}
                        {!isRetailPos && !isKioskMode && (
                            <div className="Actions">
                                <IconButton onClick={() => closePos()}>
                                    <ArrowBack fontSize="small" />
                                </IconButton>
                            </div>
                        )}
                    </div>
                    {!isKioskMode && <div className={classes.CustomerDetails}>
                        <div className="CustomerName">
                            <CustomerField />
                        </div>

                        {selectedOrder ? (
                            <IconButton
                                className={classes.Table_edit_customer}
                                onClick={() => handleEditOrderNotes()}
                            >
                                <Notes fontSize="x-small" color="white" />
                            </IconButton>
                        ) : (
                            <></>
                        )}

                        <IconButton
                            className={classes.Table_edit_customer}
                            onClick={() => changeCustomer(selectedCustomer)}
                        >
                            <PersonAddAlt1 fontSize="x-small" color="white" />
                        </IconButton>

                        {canDeleteOrder ? (
                            <IconButton
                                className={classes.Table_edit_customer}
                                onClick={() => handleDeleteOrder()}
                                disabled={isDeletingOrder}
                            >
                                <DeleteForever
                                    fontSize="x-small"
                                    color="white"
                                />
                            </IconButton>
                        ) : (
                            <></>
                        )}
                    </div>}
                </div>

                {/* <div className={classes.OrdersNav}>
                    <Scrollbar>
                        {
                            // {!isTakeaway &&
                            orders &&
                                orders.map((order) => (
                                    <StyledBreadcrumb
                                        key={`Table_Order_${order.name}`}
                                        component="a"
                                        label={getOrderShortName(order.name)}
                                        icon={
                                            selectedOrder &&
                                            order.name ===
                                                (orderName ||
                                                    selectedOrder.name) ? (
                                                <Edit />
                                            ) : null
                                        }
                                        clickable
                                        onClick={() => onSelectOrder(order)}
                                    />
                                ))
                        }
                    </Scrollbar>
                </div> */}

                {retailReturnActive ? (
                    retailReturnContent
                ) : (
                    <>
                <div className={classes.OrderItems} ref={orderItemsRef}>
                    <Scrollbar>
                        {items &&
                            items.map((item, index) => {
                                const itemKey = getItemKey(item, index);
                                return (
                                    <div
                                        key={itemKey}
                                        ref={setItemRef(itemKey)}
                                    >
                                        <OrderCartItem
                                            onItemEdit={handleEditItem}
                                            onDelete={handleDeleteItem}
                                            onSplit={onItemSplit}
                                            item={item}
                                            itemDeleting={itemDeleting}
                                            isRetailPos={isRetailPos}
                                            isHighlighted={
                                                highlightedItemKey === itemKey
                                            }
                                        />
                                    </div>
                                );
                            })}{" "}
                        {savedItems && items.length > 0 && (
                            <div className={classes.OrderCartDivider}></div>
                        )}
                        {savedItems && !isOrderItemsLoading ? (
                            savedItems
                                // Defensive: upstream code paths may occasionally
                                // produce holes (e.g. when an entry references a
                                // menu item that's no longer available). Skip
                                // them instead of crashing the whole cart.
                                .filter((s) => s && s.item)
                                .map((item, index) => (
                                    <OrderCartItem
                                        key={`OrderItem_${index}_${
                                            item.item.identifier ||
                                            item.entry_name ||
                                            item.item.item_code ||
                                            index
                                        }`}
                                        item={item}
                                        onItemEdit={
                                            isDraftOrder
                                                ? handleEditItem
                                                : undefined
                                        }
                                        onSplit={
                                            isDraftOrder
                                                ? onItemSplit
                                                : undefined
                                        }
                                        onDelete={handleDeleteSubmittedItem}
                                        isSaved={true}
                                        itemDeleting={itemDeleting}
                                        skipDeleteConfirm={
                                            isDraftOrder || isRetailPos
                                        }
                                        isRetailPos={isRetailPos}
                                        deletingItemIdentifier={
                                            deletingItemIdentifier
                                        }
                                    />
                                ))
                        ) : (
                            <LinearProgress
                                className={classes.OrderItemsProgress}
                                style={{
                                    display: isOrderItemsLoading
                                        ? "block"
                                        : "none",
                                }}
                            ></LinearProgress>
                        )}
                        {isOrderItemsLoading && isTakeaway && (
                            <LinearProgress
                                className={classes.OrderItemsProgress}
                            ></LinearProgress>
                        )}
                    </Scrollbar>
                </div>

                <div className={classes.Pricing}>
                    {(serviceFee && !isTakeaway) ||
                    selectedOrder?.discount > 0 ? (
                        <div className="Pricing__Components">
                            <div>
                                <span>{t("pos.subtotal")}</span>
                                <span>{subTotal}</span>
                            </div>{" "}
                            {serviceFee && !isTakeaway ? (
                                <div>
                                    <span>{t("pos.vat")}</span>
                                    <span>+{vat}</span>
                                </div>
                            ) : (
                                <></>
                            )}
                            {selectedOrder?.discount > 0 ? (
                                <div>
                                    <span>{t("pos.discount")}</span>
                                    <span>
                                        -{Math.ceil(selectedOrder?.discount)}
                                    </span>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className="Pricing__Total">
                        <div>
                            <span>{t("pos.total")}</span>
                            {/* TODO make sure this is correct` */}
                            <span>{total}</span>
                            {/* <span>{isTakeaway ? subTotal : total}</span> */}
                        </div>
                    </div>
                </div>
                {minified ? (
                    <div className={classes.Actions}>
                        <button className="Btn--Primary" onClick={onTableClose}>
                            {isRetailPos
                                ? t("retailPos.checkout")
                                : t("pos.closeTable")}
                        </button>
                    </div>
                ) : isKioskMode ? (
                    <div className={classes.Actions}>
                        {canShowPrimaryAction ? (
                            <>
                                <button
                                    className="Btn--Primary"
                                    onClick={onPrimaryAction}
                                    disabled={disablePrimaryActions}
                                >
                                    {primaryActionLoading ? (
                                        <CircularProgress className="CircularProgress" />
                                    ) : (
                                        primaryActionLabel
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="Btn--Primary"
                                    disabled
                                    title={t("kiosk.creditCardPlaceholder")}
                                >
                                    {t("kiosk.payCreditCard")}
                                </button>
                            </>
                        ) : (
                            <div></div>
                        )}
                    </div>
                ) : (
                    <div className={classes.Actions}>
                        {canShowPrimaryAction ? (
                            <button
                                className="Btn--Primary"
                                onClick={onPrimaryAction}
                                disabled={disablePrimaryActions}
                            >
                                {primaryActionLoading ? (
                                    <CircularProgress className="CircularProgress" />
                                ) : (
                                    primaryActionLabel
                                )}
                            </button>
                        ) : (
                            <div></div>
                        )}
                        {isRetailPos && !retailReturnActive && ((items?.length || 0) + (savedItems?.length || 0) > 0) && (
                            <button className="Btn--Primary" disabled={disablePrimaryActions || retailNotesSaving}
                                onClick={() => setRetailNotesOpen(true)}>{t("retailPos.saveWithNotes")}</button>
                        )}
                        {(!isMutating || isRetailPos) &&
                            selectedOrder &&
                            !isServiceUser &&
                            items &&
                            (isRetailPos
                                ? (savedItems?.length || 0) > 0 ||
                                  (items?.length || 0) > 0
                                : (savedItems?.length || 0) > 0) && (
                                <div className={classes.PaymentActions}>
                                    {isDraftOrder && !!handleSendDraftOrder && (
                                        <button
                                            type="button"
                                            className="Btn--Primary"
                                            onClick={handleSendDraftOrder}
                                            disabled={disableDraftOrderActions}
                                        >
                                            {isSendingDraftOrder ? (
                                                <CircularProgress className="CircularProgress" />
                                            ) : isRetailPos ? (
                                                t("retailPos.completeSale")
                                            ) : (
                                                t("pos.sendToKitchen")
                                            )}
                                        </button>
                                    )}
                                    {!isRetailPos && canMarkFoodReady && (
                                        <button
                                            type="button"
                                            className="Btn--Late"
                                            onClick={handleMarkFoodReady}
                                            disabled={
                                                markFoodReadyMutation.isLoading
                                            }
                                        >
                                            {markFoodReadyMutation.isLoading ? (
                                                <CircularProgress
                                                    size={16}
                                                    className="CircularProgress"
                                                />
                                            ) : (
                                                <>
                                                    <CheckCircleOutline fontSize="inherit" />
                                                    {t("pos.ready")}
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {!isDraftOrder && !isCallCenterUser && (
                                        <button
                                            type="button"
                                            className="Btn--Primary"
                                            onClick={
                                                restaurantSettings?.docs[0]
                                                    ?.print_order_to_pay == 1
                                                    ? selectedOrder?.invoice_printed ==
                                                      1
                                                        ? onTableClose
                                                        : togglePrintOrder
                                                    : onTableClose
                                            }
                                            disabled={isCheckoutBlocked}
                                        >
                                            {restaurantSettings?.docs[0]
                                                ?.print_order_to_pay == 1
                                                ? selectedOrder?.invoice_printed ==
                                                  1
                                                    ? isRetailPos
                                                        ? t(
                                                              "retailPos.checkout"
                                                          )
                                                        : t("pos.pay")
                                                    : isRetailPos
                                                    ? t(
                                                          "retailPos.printReceipt"
                                                      )
                                                    : t("pos.printOrder")
                                                : isRetailPos
                                                ? t("retailPos.checkout")
                                                : t("pos.pay")}
                                        </button>
                                    )}
                                </div>
                        )}
                    </div>
                )}
                    </>
                )}

                <OrderDetailsDialog
                    isOpen={orderDetailsOpen}
                    selectedCustomer={selectedCustomer}
                    title={t("pos.lastOrderDetails")}
                    toggle={toggleOrderDetails}
                />

                <PrintOrderModal
                    open={printOrderModal}
                    onClose={() => {
                        refetchTablesData().then(() => {
                            navigate("/tables");
                        });
                        togglePrintOrder();
                    }}
                    orders={orders}
                    isTakeaway={isTakeaway}
                />

                {/* Change Order dialog (tabs POS) */}
                <Dialog
                    open={changeOrderOpen}
                    onClose={toggleChangeOrder}
                    maxWidth="sm"
                    fullWidth
                >
                    <div style={{ padding: "1rem" }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "0.75rem",
                                marginBottom: "0.75rem",
                            }}
                        >
                            <div style={{ fontWeight: 800 }}>
                                {isRetailPos
                                    ? t("retailPos.selectSale")
                                    : "Select order"}
                            </div>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    toggleChangeOrder();
                                    navigate(`/pos/${tableName}`);
                                }}
                            >
                                {isRetailPos
                                    ? t("retailPos.newSale")
                                    : "New order"}
                            </Button>
                        </div>
                        <List dense>
                            {tableOrderList.map((o) => (
                                <ListItemButton
                                    key={o.name}
                                    selected={
                                        (orderName || selectedOrder?.name) ===
                                        o.name
                                    }
                                    onClick={() => {
                                        toggleChangeOrder();
                                        navigate(`/pos/${tableName}/${o.name}`);
                                    }}
                                >
                                    <ListItemText
                                        primary={getOrderShortName(o.name)}
                                        secondary={o.customer || ""}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </div>
                </Dialog>

                <CustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                    selectedCustomer={selectedCustomer}
                    customerSelected={onCustomerSelected}
                ></CustomerDialog>

                <CustomerManagementDialog
                    open={customerMgmtOpen}
                    onClose={closeCustomerMgmt}
                    selectedCustomer={selectedCustomer}
                    selectedAddress={selectedAddress}
                    selectedOrder={selectedOrder}
                    autoApplyOnSelect={true}
                    isTakeawayOverride={isTakeaway}
                    onApplyCustomerChange={(customer, address) => {
                        closeCustomerMgmt();
                        onCustomerSelected(customer, address);
                    }}
                />

                <ConfirmationDialog
                    open={deleteConfirmOpen}
                    onConfirm={confirmDeleteOrder}
                    onClose={cancelDeleteOrder}
                    title={t("pos.deleteOrderTitle")}
                    message={t("pos.deleteOrderMessage")}
                    confirmText={t("common.delete")}
                    confirmColor="error"
                    loading={isDeletingOrder}
                    item={{ elevatedPrivileges }}
                    showWasteToggle={!isRetailPos}
                />

                {retailNotesOpen && <RetailSaleNotesDialog order={selectedOrder} customer={selectedCustomer}
                total={total} loading={retailNotesSaving || isSendingToKitchen} onClose={() => setRetailNotesOpen(false)}
                onSave={async (metadata) => {
                    setRetailNotesSaving(true);
                    try {
                        await handleSaveToTable(table, { retail_metadata: metadata, onNotesSaved: () => setRetailNotesOpen(false) });
                    } finally { setRetailNotesSaving(false); }
                }} />}
            <OrderNotesDialog
                    open={orderNotesDialogOpen}
                    onClose={handleCloseOrderNotes}
                    onSave={handleSaveOrderNotes}
                    selectedOrder={selectedOrder}
                    loading={updateDoctype.isLoading}
                    isTabsPos={isTabsPos}
                    isCallCenterUser={isCallCenterUser}
                    isExistingDraft={isDraftOrder}
                />
            </div>
        </>
    );
};

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === "light"
            ? "#EFEFEF" // Custom light color
            : "#333333"; // Custom dark color
    return {
        backgroundColor,
        // color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        "&:hover, &:focus": {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        "&:active": {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
        "& .CircularProgress": {
            width: "15px !important",
            height: "15px !important",
            color: "#e14954",
        },
        padding: "5px 12px",
        background: "#6E7187",
        borderRadius: "11.25px",
        fontSize: "8px",
        lineHeight: "10px",
        textAlign: "center",
        textTransform: "uppercase",
        color: "#FFFFFF",
    };
});

export default OrderCart;
